import { Component, OnInit, ViewChild, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { PageSidesheetActions } from '@next/core-lib/components';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SiteActions } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { distinctUntilChanged } from 'rxjs/operators';

import { SiteState, TemplateTableItem, TemplateFilter } from '../shared/models';
import {
  OpenCreateTemplateDialog,
  RemoveTemplate,
  GetTemplates,
  ResetCropYear,
  OpenCopyTemplatesByYearDialog,
  ClearTemplateFilter,
  UpdateTemplateFilter,
} from '../store/actions';
import {
  selectTemplateTableItems,
  getCropYear,
  selectAvailableCropYears,
  getTemplateTotalNumberOfItems,
  getTemplatePagination,
} from '../store/selectors';

enum TemplateAction {
  edit = 'edit',
  remove = 'remove',
}

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  standalone: false,
})
export class OverviewComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly router = inject(Router);

  private subscription = new Subscription();
  cropYear$ = this.store.pipe(select(getCropYear));
  availableCropYears$ = this.store.pipe(select(selectAvailableCropYears));
  totalNumberOfItems$ = this.store.pipe(select(getTemplateTotalNumberOfItems));
  pagination$ = this.store.pipe(select(getTemplatePagination));
  showCopyTemplatesByYear = false; //disabled for now by request, see Azure devops work item 67587

  title = this.locale.translate('templates');
  displayedColumns: string[] = [
    'id',
    'isCorrupt',
    'templateType',
    'crop',
    'soilType',
    'province',
    'isSkal',
    'isPlanetProof',
    'isGroundWaterPreservation',
    'actions',
  ];
  templateActions = [
    {
      icon: 'edit',
      viewValue: this.locale.translate('edit'),
      key: TemplateAction.edit,
    },
    {
      icon: 'delete',
      viewValue: this.locale.translate('remove'),
      key: TemplateAction.remove,
    },
  ];
  dataSource = new MatTableDataSource<TemplateTableItem>();
  @ViewChild('paginator', { static: false }) paginator: MatPaginator;
  dataAvailable = false;
  yearControl = new FormControl();

  defaultPageSize = 10;
  pageSizeOptions = [10, 25, 50, 100];

  filterData: Partial<TemplateFilter> = {};
  filterEnabled: boolean;
  isMobile$ = this.breakpoint.ltLg$;

  ngOnInit(): void {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: this.title }));

    const selectTemplateSubscription = this.store
      .pipe(select(selectTemplateTableItems))
      .subscribe((result) => {
        this.dataSource.data = result;
        this.dataAvailable = result.length > 0;
        this.cdr.detectChanges();
      });

    this.subscription.add(selectTemplateSubscription);

    this.yearControl.valueChanges.subscribe((year: number) => {
      this.store.dispatch(new ResetCropYear(year));
      this.paginator.pageIndex = 0;
      this.updateData();
    });
    this.subscription.add(
      this.cropYear$.subscribe((year) => {
        this.yearControl.setValue(year, { emitEvent: false });
      }),
    );

    this.pagination$.pipe(distinctUntilChanged()).subscribe((p) => {
      if (
        p.pageNumber &&
        p.pageSize &&
        !(this.paginator.pageIndex === p.pageNumber - 1 && this.paginator.pageSize === p.pageSize)
      ) {
        this.paginator.pageIndex = p.pageNumber - 1;
        this.paginator.pageSize = p.pageSize;
        this.updateData();
      }
    });
    this.updateData();
  }

  updateData() {
    this.store.dispatch(
      new GetTemplates(
        this.paginator ? this.paginator.pageIndex + 1 : 1,
        this.paginator ? this.paginator.pageSize : this.defaultPageSize,
      ),
    );
  }

  onPage() {
    this.updateData();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  createTemplate() {
    this.store.dispatch(new OpenCreateTemplateDialog());
  }

  copyTemplatesByYear() {
    this.store.dispatch(new OpenCopyTemplatesByYearDialog());
  }

  updateFilter(data: TemplateFilter) {
    this.filterData = data;
    this.store.dispatch(new UpdateTemplateFilter(data));

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.updateData();
  }

  resetFilter() {
    this.store.dispatch(new ClearTemplateFilter());
  }

  setFilterEnabled(isEnabled: boolean) {
    this.filterEnabled = isEnabled;
  }

  setSearch() {
    this.store.dispatch(PageSidesheetActions.openSidesheet());
  }

  templateAction(key: string, id: number) {
    switch (key) {
      case TemplateAction.edit:
        this.editTemplate(id);
        break;
      case TemplateAction.remove:
        this.removeTemplate(id);
        break;
    }
  }

  editTemplate(id: number) {
    this.router.navigate(['/template', id], {
      queryParamsHandling: 'preserve',
    });
  }

  removeTemplate(id: number) {
    this.store.dispatch(new RemoveTemplate(id));
  }
}
